/**
 * Roster Home — lists Shift Planner sessions for the current user and lets them
 * create new ones. Served at the root of roster.{env}.tricorder.icu.
 *
 * Team viewing: GET /sessions returns every session the caller may read, so
 * shared rosters appear here automatically, filtered to this agent client-side
 * (the list route takes no agent filter). We split them into "My rosters"
 * (created_by me) and "Shared with me" so the planner's own rosters read
 * distinctly from ones a colleague shared. Shared rows also carry a "Shared"
 * chip (status by label, not colour alone).
 *
 * "New roster" opens the session-less planner (/sessions/new): a session is
 * created WITH its first message upstream, never empty, so the planner board
 * stands alone until the planner actually asks Troi something — the first send
 * mints the session and navigates into it.
 *
 * Design (DESIGN.md): flat hairline header (no shadow), token surfaces, the
 * single Action-Blue pill as the primary CTA (matching care-planner/alg/compatibility),
 * the always-on ReturnToMainAgent on the left edge. Four async states.
 */

import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, ChevronRight, Users } from 'lucide-react'
import { useSessions } from '@frontend/hooks/useSessions'
import { useAuthStore } from '@frontend/stores/auth'
import { Skeleton } from '@frontend/components/ui/skeleton'
import { Button } from '@frontend/components/ui/button'
import { ReturnToMainAgent } from '@frontend/components/ReturnToMainAgent'
import { MainAppNav } from '@frontend/components/MainAppNav'
import type { SessionResponse } from '@frontend/api/types_session'
import { AGENT_SLUG } from '../agent'

export default function RosterHome() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { data: sessions, isLoading } = useSessions()
  const currentUserId = useAuthStore((s: ReturnType<typeof useAuthStore.getState>) => s.user?.id ?? '')

  const handleCreate = () => {
    navigate('/sessions/new')
  }

  // Partition: a roster is "mine" when I created it; everything else readable
  // was shared TO me.
  const { mine, shared } = useMemo(() => {
    const all = (sessions ?? []).filter((s) => s.agent?.slug === AGENT_SLUG)
    const mine: SessionResponse[] = []
    const shared: SessionResponse[] = []
    for (const s of all) {
      if (s.created_by.performer !== currentUserId) shared.push(s)
      else mine.push(s)
    }
    return { mine, shared }
  }, [sessions, currentUserId])

  const renderRow = (session: SessionResponse, isShared: boolean) => (
    <li key={session.id}>
      <button
        type="button"
        onClick={() => navigate(`/sessions/${session.id}`)}
        className="flex min-h-11 w-full items-center gap-3 rounded-[18px] border border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-5 py-3 text-left transition active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:ring-offset-2"
      >
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <p className="truncate text-[17px] font-semibold text-[var(--color-surface-ink)]">
              {session.title || t('roster.untitled', 'Untitled roster')}
            </p>
            {isShared && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[var(--color-neutral-100)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-surface-mute)]">
                <Users className="h-3 w-3" aria-hidden="true" />
                {t('roster.sharedChip', 'Shared')}
              </span>
            )}
          </div>
          <p className="text-[14px] text-[var(--color-surface-mute)]">
            {new Date(session.updated_at).toLocaleString()}
          </p>
        </div>
        <ChevronRight
          className="h-5 w-5 shrink-0 text-[var(--color-surface-mute)]"
          aria-hidden="true"
        />
      </button>
    </li>
  )

  const hasAny = mine.length > 0 || shared.length > 0

  return (
    <div className="flex h-dvh flex-col bg-[var(--color-surface-canvas,#ffffff)]">
      <header className="flex items-center gap-2 border-b border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <ReturnToMainAgent className="-ml-2" />
        <h1 className="min-w-0 flex-1 truncate text-[19px] font-semibold tracking-[-0.01em] text-[var(--color-surface-ink)]">
          {t('roster.homeTitle', 'Shift Planner')}
        </h1>
        <MainAppNav />
        <Button
          size="pill"
          onClick={handleCreate}
          aria-label={t('roster.newRoster', 'New roster')}
          className="min-h-11 shrink-0"
          data-testid="roster-new-roster"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">{t('roster.newRoster', 'New roster')}</span>
        </Button>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 overflow-auto px-4 py-4">
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-[60px] w-full rounded-[18px]" />
            <Skeleton className="h-[60px] w-full rounded-[18px]" />
            <Skeleton className="h-[60px] w-full rounded-[18px]" />
          </div>
        ) : !hasAny ? (
          <div className="mx-auto flex max-w-md flex-col items-center gap-4 px-6 py-16 text-center">
            <p className="text-[17px] text-[var(--color-surface-mute)]">
              {t('roster.emptyHomeTitle', 'No rosters yet')}
            </p>
            <Button
              size="pill"
              onClick={handleCreate}
              className="min-h-11"
            >
              <Plus className="h-4 w-4" />
              {t('roster.newRoster', 'New roster')}
            </Button>
          </div>
        ) : (
          <div className="space-y-6" data-testid="roster-session-list">
            {/* My rosters — only show the heading when there's also a shared
                section, so the common single-section case stays uncluttered. */}
            {mine.length > 0 && (
              <section>
                {shared.length > 0 && (
                  <h2 className="mb-2 px-1 text-[13px] font-semibold uppercase tracking-wide text-[var(--color-surface-mute)]">
                    {t('roster.myRosters', 'My rosters')}
                  </h2>
                )}
                <ul className="space-y-3">{mine.map((s) => renderRow(s, false))}</ul>
              </section>
            )}

            {shared.length > 0 && (
              <section>
                <h2 className="mb-2 px-1 text-[13px] font-semibold uppercase tracking-wide text-[var(--color-surface-mute)]">
                  {t('roster.sharedWithMe', 'Shared with me')}
                </h2>
                <ul className="space-y-3" data-testid="roster-shared-list">
                  {shared.map((s) => renderRow(s, true))}
                </ul>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  )
}
